import type { GreyscaleImage } from './image.js';

export interface BinaryImage {
  readonly width: number;
  readonly height: number;
  /** One byte per pixel: 1 is ink, 0 is paper. */
  readonly ink: Uint8Array;
}

/**
 * Otsu's method: the threshold maximising between-class variance of the histogram.
 *
 * The midpoint of the maximising *plateau* is returned, not its first element,
 * and that is a correctness fix rather than a refinement. A drawing's histogram
 * is two narrow spikes with nothing between them, so every threshold in the gap
 * scores exactly the same between-class variance. Keeping the first one pins the
 * threshold to the ink spike itself — measured at 55 on a sheet whose ink is 55
 * and whose paper is 255 — where a single level of noise on the darkest stroke
 * drops it below the cut. The middle of the gap is the whole point of choosing a
 * threshold by variance, and it is what the plateau midpoint gives.
 */
export function otsuThreshold(image: GreyscaleImage): number {
  const histogram = new Float64Array(256);
  for (const value of image.pixels) histogram[value] = histogram[value]! + 1;
  const total = image.pixels.length;

  let sum = 0;
  for (let i = 0; i < 256; i += 1) sum += i * histogram[i]!;

  let weightBelow = 0;
  let sumBelow = 0;
  let plateauStart = 0;
  let plateauEnd = 0;
  let bestVariance = -1;

  for (let t = 0; t < 256; t += 1) {
    weightBelow += histogram[t]!;
    if (weightBelow === 0) continue;
    const weightAbove = total - weightBelow;
    if (weightAbove === 0) break;
    sumBelow += t * histogram[t]!;
    const meanBelow = sumBelow / weightBelow;
    const meanAbove = (sum - sumBelow) / weightAbove;
    const variance = weightBelow * weightAbove * (meanBelow - meanAbove) ** 2;
    if (variance > bestVariance) {
      bestVariance = variance;
      plateauStart = t;
      plateauEnd = t;
    } else if (variance === bestVariance) {
      plateauEnd = t;
    }
  }
  return Math.floor((plateauStart + plateauEnd) / 2);
}

export function binariseGlobal(image: GreyscaleImage, threshold = otsuThreshold(image)): BinaryImage {
  const ink = new Uint8Array(image.pixels.length);
  for (let i = 0; i < image.pixels.length; i += 1) ink[i] = image.pixels[i]! <= threshold ? 1 : 0;
  return { width: image.width, height: image.height, ink };
}

/**
 * A local threshold, and what it is actually for.
 *
 * Each pixel is compared with the mean of its neighbourhood, computed from a
 * summed-area table so the window size costs nothing. One global threshold
 * cannot serve both ends of a steeply lit sheet: set for the lit corner it
 * floods the dark one with ink, and set for the dark corner it erases the lit
 * one.
 *
 * **The corpus is not that sheet, and the measurement says so.** The noisy
 * fixtures carry `illumination: 46`, and 46 levels does not outrun a 200-level
 * ink-to-paper separation — RF01.300.noisy binarises to 0.0210 ink globally
 * against the clean sheet's 0.0165. The crossover on the synthetic sheet in the
 * tests is somewhere between gradient 120 and 180. So this path is insurance for
 * a worse scan than the corpus contains, which is worth carrying and worth not
 * overstating.
 *
 * `bias` is what keeps flat paper white, and its default is measured rather than
 * assumed. A 31x31 local mean is essentially noise-free, so on blank paper a
 * pixel is called ink whenever its own noise dips `bias` below that mean — a
 * probability of `1 - Phi(bias / sigma)`. At the corpus's `noise: 12`, applied
 * after the blur and so unsmoothed, the sweep on blank paper measured:
 *
 *     bias   10      20      30      36      40
 *     ink    0.1925  0.0441  0.0049  0.0008  0.0000
 *
 * and on the real sheets, RF01.300.noisy measured 0.1129 ink at bias 10 against
 * 0.0229 at bias 40, where the truth is near the clean sheet's 0.0174. A bias of
 * 10 therefore reports five times the ink a scan contains, which is a Hough
 * transform full of spurious peaks — a threshold defect wearing a detector's
 * clothes. 40 is a little over three sigma, and it costs 3% of the ink on the
 * clean sheets (0.0179 to 0.0174) to buy that.
 */
export function binariseAdaptive(
  image: GreyscaleImage,
  { window = 31, bias = 40 }: { window?: number; bias?: number } = {},
): BinaryImage {
  const { width, height, pixels } = image;
  const radius = Math.max(1, Math.floor(window / 2));

  // Summed-area table, one row and column of zero padding. Float64 rather than
  // Uint32 because a 600 DPI A4 sheet sums past 2^32: 34.8M pixels at 255 is
  // 8.9e9, and every integer that size is still exact in a double.
  const integral = new Float64Array((width + 1) * (height + 1));
  for (let y = 0; y < height; y += 1) {
    let rowSum = 0;
    for (let x = 0; x < width; x += 1) {
      rowSum += pixels[y * width + x]!;
      integral[(y + 1) * (width + 1) + (x + 1)] = integral[y * (width + 1) + (x + 1)]! + rowSum;
    }
  }

  const ink = new Uint8Array(pixels.length);
  for (let y = 0; y < height; y += 1) {
    const y0 = Math.max(0, y - radius);
    const y1 = Math.min(height - 1, y + radius);
    for (let x = 0; x < width; x += 1) {
      const x0 = Math.max(0, x - radius);
      const x1 = Math.min(width - 1, x + radius);
      const area = (x1 - x0 + 1) * (y1 - y0 + 1);
      const sum =
        integral[(y1 + 1) * (width + 1) + (x1 + 1)]! -
        integral[y0 * (width + 1) + (x1 + 1)]! -
        integral[(y1 + 1) * (width + 1) + x0]! +
        integral[y0 * (width + 1) + x0]!;
      const mean = sum / area;
      ink[y * width + x] = pixels[y * width + x]! <= mean - bias ? 1 : 0;
    }
  }
  return { width, height, ink };
}

export function inkFraction(image: BinaryImage): number {
  let count = 0;
  for (const value of image.ink) count += value;
  return count / image.ink.length;
}
